import type { StatusTone } from "./residents";

/** What the pass report says about a resident, beyond the counts. Here rather than in the API
 * because the report is read in three places — the screen, the CSV and the PDF — and a flag whose
 * label lives beside the query is a flag the screen renders as `NO_SUPPLY`. */
export const MED_PASS_FLAGS = [
  "ZERO", // nothing charted at all in the period: the forgotten resident
  "MISSED", // sweep-materialized missed dose(s)
  "NO_SUPPLY", // a "not available" exception: an operational failure
  "VOIDED", // an administration was voided in this period
  "AMENDED", // an administration was amended in this period
  "REFUSED", // repeated refusals of the same drug
  "LATE", // gives past the window beyond grace
  "PRN_HIGH", // as-needed order at or near its 24h ceiling
  "FU_LAPSED", // as-needed follow-up never resulted
  "NO_SCHEDULE", // active routine order with no schedule rows: never passed
  "UNANSWERED", // slots still open, meaningful mostly for the current day
  "ON_HOLD", // orders on hold: context for a small denominator
] as const;

export type MedPassFlag = (typeof MED_PASS_FLAGS)[number];

export const MED_PASS_FLAG_LABELS: Record<MedPassFlag, string> = {
  ZERO: "Nothing charted",
  MISSED: "Missed",
  NO_SUPPLY: "Not available",
  VOIDED: "Voided",
  AMENDED: "Amended",
  REFUSED: "Repeat refusals",
  LATE: "Late",
  PRN_HIGH: "PRN high use",
  FU_LAPSED: "Follow-up lapsed",
  NO_SCHEDULE: "No schedule",
  UNANSWERED: "Still open",
  ON_HOLD: "On hold",
};

/** What each one costs, as a colour. A dose nobody charted and a dose the pharmacy could not supply
 * are both findings; an amendment is a correction somebody already made, and orders on hold are
 * context for a small denominator rather than a problem with it. */
export const MED_PASS_FLAG_TONES: Record<MedPassFlag, StatusTone> = {
  ZERO: "crit",
  MISSED: "crit",
  NO_SUPPLY: "crit",
  NO_SCHEDULE: "crit",
  FU_LAPSED: "crit",
  LATE: "warn",
  REFUSED: "warn",
  PRN_HIGH: "warn",
  UNANSWERED: "warn",
  VOIDED: "info",
  AMENDED: "info",
  ON_HOLD: "neutral",
};

/** What a flag means, for the legend the report carries. */
export const MED_PASS_FLAG_HELP: Record<MedPassFlag, string> = {
  ZERO: "Nothing was charted for this resident at all in the period.",
  MISSED: "A dose was recorded as missed.",
  NO_SUPPLY: "A dose could not be given because the medication was not available.",
  VOIDED: "An administration was voided. The entry is a correction, not an event.",
  AMENDED: "An administration was amended after it was first recorded.",
  REFUSED: "The same drug was refused more than once.",
  LATE: "A dose was given past its window, beyond the grace period.",
  PRN_HIGH: "An as-needed order is at or near its 24-hour ceiling.",
  FU_LAPSED: "An as-needed dose was given and nobody recorded whether it worked.",
  NO_SCHEDULE: "An active routine order has no schedule, so it was never passed.",
  UNANSWERED: "Slots are still open. Expected for today, a finding for a past day.",
  ON_HOLD: "Orders are on hold, which is why the denominator is small.",
};
